'use client';

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Images } from 'lucide-react';
import { AdaptiveDialog } from '@/shared/ui/AdaptiveDialog';
import { Typography } from '@/shared/ui/shadcn/typography';
import { useProjectAngles } from '@/shared/api/hooks/use-project-angles';
import { conceptsService } from '@/shared/api/services/concepts.service';
import { useLimitErrorPaywall } from '@/shared/hooks/useLimitErrorPaywall';
import { useSubscriptionLimits } from '@/features/subscription';
import { useCreateConcept } from '@/features/design/concept/hooks/use-concepts';
import { isProjectAngleValid, type ProjectAngle } from '@/types/project-angle';
import { AngleSelectionRow } from '@/features/angles-overview/ui/AngleSelectionRow';
import type { ConceptAngle } from '@/types/concept';

// Discriminated union: the modal serves two flows that share the same UI —
// creating a brand-new concept (no conceptId yet), and editing an existing
// concept's angle set. Only the mutation target and the wording differ.
type ConceptAnglesEditModalProps = {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
} & (
  | {
      mode: 'edit';
      conceptId: string;
      conceptAngles: ConceptAngle[];
      // Receives angle IDs newly attached relative to the baseline when the
      // modal opened, so the parent can surface the new derivative.
      onComplete: (newlyAttachedAngleIds: string[]) => void;
    }
  | {
      mode: 'create';
      // Called after the new concept is created server-side. Parent is
      // expected to navigate to the concept page.
      onCreated: (conceptId: string) => void;
      /**
       * Angle ids to pre-select on open. Used by the post-upload finalize
       * handoff: the project page uploads images as project-angles, then opens
       * this modal with the freshly uploaded ids prefilled so the user picks
       * main + reorder without manually searching the library.
       */
      prefilledAngleIds?: string[];
    }
);

export function ConceptAnglesEditModal(props: ConceptAnglesEditModalProps) {
  const { projectId, open, onOpenChange } = props;
  const mode = props.mode;
  // Pre-extract mode-specific handlers so the submit callback doesn't have to
  // re-narrow the discriminated union on every call. Branching on `props.mode`
  // keeps TypeScript's discriminated-union narrowing intact.
  const editConceptId = props.mode === 'edit' ? props.conceptId : undefined;
  const editOnComplete = props.mode === 'edit' ? props.onComplete : undefined;
  const createOnCreated = props.mode === 'create' ? props.onCreated : undefined;
  const t = useTranslations('angles');
  const tCommon = useTranslations('common');

  const { data: angles = [] } = useProjectAngles(projectId);

  // Per-concept angle cap from the effective plan (real subscription OR guest
  // fallback computed server-side). Guards the toggle so users can't select
  // past the plan limit between open and Save.
  const limits = useSubscriptionLimits();
  const maxAnglesAllowed = Math.max(1, (limits?.angles as number) ?? 2);

  // Baseline: in edit mode it's the server-truth angle_ids when the modal was
  // opened; in create mode there's nothing to compare against, so an empty set.
  const conceptAngles =
    props.mode === 'edit' ? props.conceptAngles : undefined;
  const baselineAngleIds = useMemo(() => {
    if (!conceptAngles) return [] as string[];
    return conceptAngles
      .map((ca) => ca.relationships?.angle?.data?.id)
      .filter((id): id is string => !!id);
  }, [conceptAngles]);

  // create mode only: angle ids the caller wants prefilled on open
  // (post-upload finalize handoff). Falls back to empty.
  const prefilledAngleIds = useMemo(
    () => (props.mode === 'create' ? props.prefilledAngleIds ?? [] : []),
    [props.mode === 'create' ? props.prefilledAngleIds : undefined]
  );

  // Draft: ordered project-angle IDs. First element is main, rest are derivatives.
  // All toggle / reorder interactions mutate this list only — no network traffic
  // until the user hits Save/Create. Cancel just closes the modal and the draft dies.
  const [draftAngleIds, setDraftAngleIds] =
    useState<string[]>(baselineAngleIds);
  const [isSaving, setIsSaving] = useState(false);

  const validAngles = useMemo(
    () => angles.filter(isProjectAngleValid),
    [angles]
  );

  // Re-seed draft when the modal (re)opens with fresh server data. Without this,
  // a stale draft from a previous open could leak in on reopen. In create mode
  // the post-upload handoff seeds the draft with the freshly-uploaded ids
  // (prefilledAngleIds) so the user lands on the sheet with their just-taken
  // photos already selected and ordered (first = main).
  useEffect(() => {
    if (!open) return;
    if (mode === 'create' && prefilledAngleIds.length > 0) {
      // Filter against validAngles so prefilled ids that haven't completed
      // server-side validation yet (or that came back invalid) don't show as
      // "selected" — the row wouldn't render and the count would lie.
      // Mirrors Apple ConceptAnglesView and Android ConceptAnglesViewModel.
      const validIds = new Set(validAngles.map((a) => a.id));
      const filtered = prefilledAngleIds.filter((id) => validIds.has(id));
      // Cap to the per-concept limit; the picker already enforces this for
      // toggles but the prefill comes from a server-side upload where the
      // library cap is the only constraint.
      setDraftAngleIds(filtered.slice(0, maxAnglesAllowed));
    } else {
      setDraftAngleIds(baselineAngleIds);
    }
  }, [open, baselineAngleIds, mode, prefilledAngleIds, maxAnglesAllowed, validAngles]);

  const selectedAngles = useMemo(
    () =>
      draftAngleIds
        .map((id) => validAngles.find((a) => a.id === id))
        .filter((a): a is ProjectAngle => !!a),
    [draftAngleIds, validAngles]
  );

  const unselectedAngles = useMemo(
    () => validAngles.filter((a) => !draftAngleIds.includes(a.id)),
    [validAngles, draftAngleIds]
  );

  const hasChanges = useMemo(
    () =>
      draftAngleIds.length !== baselineAngleIds.length ||
      draftAngleIds.some((id, i) => id !== baselineAngleIds[i]),
    [draftAngleIds, baselineAngleIds]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDraftAngleIds((current) => {
      const oldIndex = current.indexOf(String(active.id));
      const newIndex = current.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return current;
      return arrayMove(current, oldIndex, newIndex);
    });
  }, []);

  const handleToggle = useCallback(
    (projectAngleId: string) => {
      setDraftAngleIds((current) => {
        const index = current.indexOf(projectAngleId);
        if (index === -1) {
          // At the per-concept limit — ignore further additions. The UI also
          // dims unselected rows to make this state visible.
          if (current.length >= maxAnglesAllowed) return current;
          return [...current, projectAngleId];
        }
        if (mode === 'edit' && index === 0) {
          // Edit mode: main is the first item — removing it is disallowed. User
          // promotes a new main by reordering (drag the new main into slot 0).
          return current;
        }
        return current.filter((id) => id !== projectAngleId);
      });
    },
    [maxAnglesAllowed, mode]
  );

  const isAtLimit = draftAngleIds.length >= maxAnglesAllowed;

  const routeLimitError = useLimitErrorPaywall();
  const createConceptMutation = useCreateConcept();

  const handleSubmit = useCallback(async () => {
    if (isSaving || draftAngleIds.length === 0) return;
    if (mode === 'edit' && !hasChanges) return;
    setIsSaving(true);
    try {
      if (mode === 'edit' && editConceptId && editOnComplete) {
        const items = draftAngleIds.map((angleId, index) => ({
          angle_id: angleId,
          type: index === 0 ? ('main' as const) : ('derivative' as const),
        }));
        await conceptsService.syncConceptAngles(editConceptId, items);
        onOpenChange(false);
        const baseline = new Set(baselineAngleIds);
        const newlyAttached = draftAngleIds.filter((id) => !baseline.has(id));
        editOnComplete(newlyAttached);
      } else if (mode === 'create' && createOnCreated) {
        const [mainAngleId, ...derivativeAngleIds] = draftAngleIds;
        if (!mainAngleId) return;
        const newConcept = await createConceptMutation.mutateAsync({
          projectId,
          mainAngleId,
          derivativeAngleIds,
        });
        onOpenChange(false);
        createOnCreated(newConcept.id);
      }
    } catch (err) {
      if (!routeLimitError(err)) {
        console.error('Failed to submit concept angles:', err);
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    isSaving,
    hasChanges,
    draftAngleIds,
    mode,
    projectId,
    baselineAngleIds,
    onOpenChange,
    createConceptMutation,
    routeLimitError,
    editConceptId,
    editOnComplete,
    createOnCreated,
  ]);

  const handleCancel = useCallback(() => {
    // Discard the draft; server state is untouched.
    onOpenChange(false);
  }, [onOpenChange]);

  const router = useRouter();
  // Empty-state ("No views") link: close the modal and open the project's Views
  // library so the user can add views before creating a concept — parity with
  // Apple/Android, which point to the library rather than a disk upload here.
  const handleGoToViews = useCallback(() => {
    onOpenChange(false);
    router.push(`/project/${projectId}/angles`);
  }, [onOpenChange, router, projectId]);

  const titleKey =
    mode === 'create' ? 'conceptAngles.createTitle' : 'conceptAngles.title';
  const submitLabel =
    mode === 'create'
      ? t('conceptAngles.createConcept')
      : tCommon('save');
  const submitDisabled =
    isSaving ||
    draftAngleIds.length === 0 ||
    (mode === 'edit' && !hasChanges);

  return (
    <AdaptiveDialog
      open={open}
      onClose={handleCancel}
      title={t(titleKey)}
      onSave={handleSubmit}
      saveEnabled={!submitDisabled}
      isSaving={isSaving}
      saveLabel={submitLabel}
    >
      {validAngles.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Images className="w-16 h-16 text-muted-foreground mb-4" />
          <Typography variant="h5" className="mb-2">
            {t('conceptAngles.noValidAngles')}
          </Typography>
          <Typography variant="muted" className="max-w-sm">
            {t.rich('conceptAngles.noValidAnglesDescription', {
              link: (chunks) => (
                <button
                  type="button"
                  onClick={handleGoToViews}
                  className="text-primary underline underline-offset-2 hover:opacity-80"
                >
                  {chunks}
                </button>
              ),
            })}
          </Typography>
        </div>
      ) : (
        <div className="flex flex-col gap-4 pt-2">
          {selectedAngles.length > 0 && (
            <div>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={draftAngleIds}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-1">
                    {selectedAngles.map((angle, idx) => (
                      <AngleSelectionRow
                        key={angle.id}
                        angle={angle}
                        index={validAngles.indexOf(angle)}
                        isSelected={true}
                        selectionIndex={idx}
                        positionLabel={
                          idx < 7
                            ? t(`conceptAngles.position.${idx + 1}`)
                            : undefined
                        }
                        onToggle={() => handleToggle(angle.id)}
                        sortable={true}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          )}

          {unselectedAngles.length > 0 && (
            <div>
              <Typography
                variant="small"
                className="text-muted-foreground uppercase tracking-wide font-semibold mb-2"
              >
                {t('conceptAngles.availableSection')}
              </Typography>
              <div className="space-y-1">
                {unselectedAngles.map((angle) => (
                  <AngleSelectionRow
                    key={angle.id}
                    angle={angle}
                    index={validAngles.indexOf(angle)}
                    isSelected={false}
                    selectionIndex={null}
                    disabled={isAtLimit}
                    onToggle={() => handleToggle(angle.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Info banner: hint + "selected / max" capsule. Matches Android
              SelectionHeader — capsule shows the per-concept cap so the limit is
              visible before hitting Save. Placed at the bottom under the angle
              sections. */}
          <div className="flex items-center justify-between rounded-md bg-muted/50 px-4 py-3">
            <Typography variant="muted" className="text-form-help !mt-0">
              {t('conceptAngles.selectHint')}
            </Typography>
            <span className="inline-flex items-center rounded-full bg-primary/15 px-3 py-0.5 text-list-item-value text-primary">
              {draftAngleIds.length}/{maxAnglesAllowed}
            </span>
          </div>
        </div>
      )}
    </AdaptiveDialog>
  );
}
